import React, { useState, useEffect } from 'react';
import { FaCheckCircle, FaExclamationCircle, FaInfoCircle, FaTimes } from 'react-icons/fa';
import './ModernSnackbar.css';

const ModernSnackbar = ({ 
  message, 
  type = 'info', 
  duration = 4000, 
  onClose, 
  position = 'top-right' 
}) => {
  const [isVisible, setIsVisible] = useState(true);
  const [isExiting, setIsExiting] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      handleClose();
    }, duration);

    return () => clearTimeout(timer);
  }, [duration]);

  const handleClose = () => {
    setIsExiting(true);
    setTimeout(() => {
      setIsVisible(false);
      onClose && onClose();
    }, 300);
  };

  const getIcon = () => {
    switch (type) {
      case 'success':
        return <FaCheckCircle className="snackbar-icon success" />;
      case 'error':
        return <FaExclamationCircle className="snackbar-icon error" />;
      case 'warning':
        return <FaExclamationCircle className="snackbar-icon warning" />;
      default:
        return <FaInfoCircle className="snackbar-icon info" />;
    }
  };

  const getTypeClass = () => {
    switch (type) {
      case 'success':
        return 'snackbar-success';
      case 'error':
        return 'snackbar-error';
      case 'warning':
        return 'snackbar-warning';
      default:
        return 'snackbar-info';
    }
  };

  if (!isVisible) return null;

  return (
    <div className={`modern-snackbar ${getTypeClass()} ${position} ${isExiting ? 'exiting' : ''}`}>
      <div className="snackbar-content">
        {getIcon()}
        <span className="snackbar-message">{message}</span>
        <button 
          className="snackbar-close" 
          onClick={handleClose}
          aria-label="Close notification"
        >
          <FaTimes />
        </button>
      </div>
      <div className="snackbar-progress">
        <div className="snackbar-progress-bar"></div>
      </div>
    </div>
  );
};

// Snackbar Manager Component
export const SnackbarManager = () => {
  const [snackbars, setSnackbars] = useState([]);

  const addSnackbar = (message, type = 'info', duration = 4000, position = 'top-right') => {
    const id = Date.now() + Math.random();
    const newSnackbar = { id, message, type, duration, position };
    
    setSnackbars(prev => [...prev, newSnackbar]);
    
    return id;
  };

  const removeSnackbar = (id) => {
    setSnackbars(prev => prev.filter(snackbar => snackbar.id !== id));
  };

  // Global function to show snackbars
  window.showSnackbar = addSnackbar;

  return (
    <div className="snackbar-container">
      {snackbars.map((snackbar) => (
        <ModernSnackbar
          key={snackbar.id}
          message={snackbar.message}
          type={snackbar.type}
          duration={snackbar.duration}
          position={snackbar.position}
          onClose={() => removeSnackbar(snackbar.id)}
        />
      ))}
    </div>
  );
};

// Convenience functions
export const showSuccess = (message, duration = 4000) => {
  if (window.showSnackbar) {
    window.showSnackbar(message, 'success', duration);
  }
};

export const showError = (message, duration = 4000) => {
  if (window.showSnackbar) {
    window.showSnackbar(message, 'error', duration);
  }
};

export const showWarning = (message, duration = 4000) => {
  if (window.showSnackbar) {
    window.showSnackbar(message, 'warning', duration);
  }
};

export const showInfo = (message, duration = 4000) => {
  if (window.showSnackbar) {
    window.showSnackbar(message, 'info', duration);
  }
};

export default ModernSnackbar; 